// src/oura/nav.ts
//
// Router-aware navigation for the Oura product. The prototype drove screens
// from App-level state; inside ourteam-frontend the URL is the state, so this
// module maps the same View vocabulary onto /oura/* routes and builds the
// sidebar groups per role + drill-in, exactly like the prototype's navFor.

import type { LucideIcon } from "lucide-react";
// Nav glyphs: Lucide outline, one plain idea each (no check-mark or badge
// add-ons, so Site is a globe and Delivery a box), the literal meaning
// (History is a clock, not a trend line), and the same glyph wherever the
// same page appears. Colour comes from the row, never the icon (NAV in
// ui/tokens.ts).
import {
    LayoutDashboardIcon,
    ListChecksIcon as DecisionsIcon,
    FileTextIcon,
    Building2Icon,
    EyeIcon,
    GlobeIcon,
    ClipboardListIcon,
    PlugIcon,
    HistoryIcon,
    MessageSquareIcon,
    FolderOpenIcon,
    HomeIcon,
    PackageIcon,
    KeyRoundIcon,
    FlagIcon,
    WandSparklesIcon,
} from "lucide-react";
import type { OuraRole } from "./role";
import { isOuraProd } from "./role";
import { MERCHANT_V2 } from "./merchantV2";
import { DELIVERY_ON } from "./delivery/flag";
import { isClientReviewLive } from "@service/aeo/clientReview";
import { isOnboardingLive } from "@service/aeo/onboarding";
import { DOCS_WRITE_ON } from "./docs/writeFlag";
import { REQUESTS_ON } from "./requests/flag";

/**
 * Onboarding runs on sample data until its endpoints ship, so production
 * hides it: no real user is shown a sample client as their own.
 */
export const showOnboarding: boolean = isOnboardingLive || !isOuraProd();

export type View =
    | "dashboard"
    | "questions"
    | "pages" // staff drill-in: page selection + copy/link workshop
    | "search" // search performance from Search Console (ourteam-backend#3818)
    | "setup"
    | "todo"
    | "delivery" // staff drill-in: what we owe the client, deliverable by deliverable (replaces Fixes and Plan)
    | "reviews" // staff drill-in: the client approval layer over the Tracker
    | "decisions" // client: packs waiting for an answer
    | "reports" // client: every report sent
    | "work" // client: their dated plan and the work in it (the old Schedule, #3715)
    // client, retired: /oura/schedule redirects to /oura/work. Kept so a stale
    // link or an old `ouraHref("schedule")` still lands somewhere.
    | "schedule"
    | "documents" // staff drill-in: every document made or shared for the client (Documents, first tab)
    | "knowledge" // staff drill-in: what the business told us, off-site (Documents, second tab)
    | "onboarding" // staff drill-in: from a signed quote to an active client
    | "connectors" // staff drill-in: data-source connectors
    | "contract" // staff drill-in: what was sold, as a contract (#3714)
    | "timeline" // staff drill-in: the dated schedule from the engine (#3714)
    | "activity" // staff drill-in: the business's delivery record (#4508, #4531)
    | "history" // staff drill-in: past assessment runs (gameplan A4)
    | "onboarding" // staff drill-in: from a signed quote to an active client
    | "access" // client: the access they give us, and how to remove it
    | "team" // client: who can sign in to their account, and inviting a colleague
    | "overview" // staff roll-up (home)
    | "businesses" // staff list
    | "skills" // staff top-level: the Skill library, 1 slot per doc type (Docs phase 2, only where DOCS_WRITE_ON)
    | "assessment" // staff top-level: AEO Assessment prescreen/create/poll/report (#3065)
    | "board" // staff top-level: the Tracker, every client's delivery on one page (only where DELIVERY_ON)
    | "requests" // staff top-level: every client's change requests, from any channel (only where REQUESTS_ON)
    | "formAnswers" // staff top-level: every onboarding form answer and whether it arrived (#4065)
    | "settings"
    | "report";

export const OURA_ROOT = "/oura";

// Oura reuses the shared Settings surface (Personal Information + Workspace
// Members) exactly as Trainer does — one /setting route — rather than forking a
// Oura-only page. The product hint keeps the Oura shell + tab set.
export const OURA_SETTINGS_PATH = "/setting?product=oura";
/** Settings > Manage Team Access: who can sign in, and inviting a colleague. The merchant's old Team page lives here now. */
export const OURA_TEAM_ACCESS_PATH = `${OURA_SETTINGS_PATH}&tab=user-permissions`;

export interface OuraNavItem {
    view: View;
    label: string;
    icon: LucideIcon;
    to: string;
    /**
     * What the page is for, shown on hover and keyboard focus (#4026 C-FE-5).
     * Nothing said what a sidebar item did before it was clicked, and the
     * tester asked.
     *
     * The client rail carries copy; the staff rail does not yet, and an item
     * with no hint shows no tooltip, so nothing changes there. The same
     * sentence is the page's own description (C-FE-6), so the tooltip and the
     * page agree by construction rather than by someone remembering.
     */
    hint?: string;
    /** Other pages this item stays lit on, reached from inside it (client Search performance). */
    alsoActive?: string[];
}
/**
 * What each client page is for, in the client's own terms.
 *
 * ONE SOURCE for two surfaces: the sidebar tooltip (#4026 C-FE-5) and the
 * description under the page's own title (C-FE-6). They said the same thing in
 * the ticket, so they say it from the same constant here -- otherwise they
 * drift the first time one of them is reworded.
 *
 * The Visibility line carries no counting rule: the rule is per scan
 * (namedRule), and a fixed "any AI engine" here contradicted the cards on a
 * scan that did not count Google's AI (usability test, 14 Oct 2026). The
 * headline tile, the trend and the type grid state it, in the client's words.
 */
/** The seven pages a client sees. */
type ClientView = "dashboard" | "questions" | "setup" | "work" | "decisions" | "reports" | "access";

/**
 * The redesigned rail's hints (#4062 MR-FE-2): four or five words, read in
 * passing. CLIENT_HINT below is the long form, which stays as the sentence
 * under each page's own title -- a tooltip and a page description are read at
 * different moments and want different lengths.
 */
export const CLIENT_HINT_V2: Record<ClientView, string> = {
    dashboard: "Your month at a glance",
    decisions: "Questions for you",
    work: "What we are doing",
    questions: "How AI finds you",
    setup: "How AI reads your site",
    reports: "Everything we have sent you",
    access: "Access you gave us",
};

export const CLIENT_HINT: Record<ClientView, string> = {
    dashboard: "What needs you, and how the month is going.",
    questions: "Where AI assistants and Google name you, and who they name instead.",
    setup: "How Google and AI read your site, and what we found on it.",
    work: "What we are doing for you this month, and what is done.",
    decisions: "Questions and documents waiting for your answer.",
    reports: "Every report we have sent you, and when the next one comes.",
    access: "The access you have given us, and what is still needed.",
};

export interface OuraNavGroup {
    label: string;
    items: OuraNavItem[];
}

/** Where a given View lives, given who you are and which business is in focus. */
export function ouraHref(
    view: View,
    ctx: { role: OuraRole; businessId?: string | null },
): string {
    if (view === "settings") return OURA_SETTINGS_PATH;
    if (view === "overview") return `${OURA_ROOT}/overview`;
    if (view === "businesses") return `${OURA_ROOT}/businesses`;
    if (view === "skills") return `${OURA_ROOT}/skills`;
    if (view === "assessment") return `${OURA_ROOT}/assessment`;
    if (view === "formAnswers") return `${OURA_ROOT}/form-answers`;
    if (view === "requests") return `${OURA_ROOT}/requests`;
    if (view === "board") return `${OURA_ROOT}/tracker`;

    // Staff always operate on a business reached from the list, so their
    // single-business views are nested under that business id.
    if (ctx.role === "oura-staff" && ctx.businessId) {
        const base = `${OURA_ROOT}/businesses/${ctx.businessId}`;
        return view === "dashboard" ? base : `${base}/${view}`;
    }

    // Client (and any staff fallback): the single-business views live at the root.
    return view === "dashboard" ? `${OURA_ROOT}/dashboard` : `${OURA_ROOT}/${view}`;
}

/** The single-business tabs — the whole client nav, and staff's drilled-in nav. */
function singleBusinessGroups(
    role: OuraRole,
    businessId?: string | null,
): OuraNavGroup[] {
    const href = (v: View) => ouraHref(v, { role, businessId });
    // Five items. Home, Work, Decisions and Reports are the delivery loop:
    // where things stand, what we are doing and when, what we need answered,
    // what we have sent. Work took over Schedule's route and all of its
    // content, so the dates are no longer a nav item of their own.
    //
    // Visibility is the evidence under all of it, restored to the rail (YW,
    // 11 Sep 2026). The screen was never deleted — the route resolved the whole
    // time — but its only way in was a "Detail" link on a three-line card, and
    // it is the one surface that shows the merchant the thing they are paying
    // for: the score, which engines name them, and who gets named instead.
    // A product about AI visibility cannot keep its only measurement of it
    // behind a footnote.
    //
    // Website sits beside Visibility: the site findings staff read on Site,
    // read-only, on the address the old sample-only "Your setup" had. To-do
    // and Search stay out: To-do is what Decisions replaced, and Search opens
    // from the Google tiles on Home and Visibility. Both still resolve.
    // Until the approval-layer endpoints ship (ourteam-backend#4735) production
    // keeps the older client screens; staging and dev show the new three.
    // Set-up is its own switch: once onboarding is live the merchant keeps a
    // way to see what we hold, whichever client screens are showing.
    // The redesign renames three items and reorders the rail (#4062 MR-FE-2).
    // VIEWS AND ADDRESSES DO NOT MOVE -- only the words and the order -- so
    // every link, email and bookmark already sent to a client still resolves.
    const label = (today: string, next: string) => (MERCHANT_V2 ? next : today);
    const hint = (view: ClientView) => (MERCHANT_V2 ? CLIENT_HINT_V2[view] : CLIENT_HINT[view]);

    const setup: OuraNavItem[] = showOnboarding
        ? [{ view: "access", label: label("Set-up", "Setup"), icon: KeyRoundIcon, to: href("access"), hint: hint("access") }]
        : [];
    if (isOuraProd() && !isClientReviewLive) {
        return [
            { label: "Overview", items: [{ view: "dashboard", label: "Dashboard", icon: LayoutDashboardIcon, to: href("dashboard") }, ...setup] },
        ];
    }
    // The view keeps its name and its URL (/oura/dashboard): every link, email
    // and bookmark to it predates the label.
    // Our work is part of Home in the redesign, so its old address lights Home.
    const home: OuraNavItem = {
        view: "dashboard",
        label: "Home",
        icon: HomeIcon,
        to: href("dashboard"),
        hint: hint("dashboard"),
        ...(MERCHANT_V2 ? { alsoActive: [href("work")] } : {}),
    };
    // Same icon the staff sidebar uses for the same data, so the two sides of
    // the product name it identically.
    // The redesign's five-item rail (Home, Visibility, To do, Our work,
    // Reports): Website is hidden for now (its address still resolves, and
    // lights Visibility if reached), and set-up is a tab inside To do.
    const visibility: OuraNavItem = {
        view: "questions",
        label: "Visibility",
        icon: EyeIcon,
        to: href("questions"),
        hint: hint("questions"),
        alsoActive: MERCHANT_V2 ? [href("search"), href("setup")] : [href("search")],
    };
    // The client's Website page: the staff Site screen's findings, read-only,
    // on the same view and address (/oura/setup).
    // The globe staff Site uses: the same data, the same glyph.
    const website: OuraNavItem = { view: "setup", label: "Website", icon: GlobeIcon, to: href("setup"), hint: hint("setup") };
    const ourWork: OuraNavItem = { view: "work", label: label("Work", "Our work"), icon: ClipboardListIcon, to: href("work"), hint: hint("work") };
    const toDo: OuraNavItem = {
        view: "decisions",
        label: label("Decisions", "To do"),
        icon: DecisionsIcon,
        to: href("decisions"),
        hint: hint("decisions"),
        ...(MERCHANT_V2 ? { alsoActive: [href("access")] } : {}),
    };
    const reports: OuraNavItem = { view: "reports", label: MERCHANT_V2 ? "Documents" : "Reports", icon: FileTextIcon, to: href("reports"), hint: hint("reports") };

    return [
        {
            label: "Overview",
            // THE ORDER IS THE READING ORDER: Home, Visibility, To do,
            // Reports. Visibility sits under Home, beside the AI tile that
            // opens it (15 Oct 2026). Our work is part of Home (16 Oct 2026):
            // /oura/work still resolves, and opens Home.
            items: MERCHANT_V2
                ? [home, visibility, toDo, reports]
                // (Website stays defined above for the older rail.)
                // Last: set once, then the place to see what we hold and remove it.
                : [home, visibility, website, ourWork, toDo, reports, ...setup],
        },
    ];
}

/* ------------------------------------------------------------------------- *
 * The per-business sidebar (replaces the drill-in tab strip)
 * ------------------------------------------------------------------------- */

/**
 * Nine tab labels named after our tables — Pages, Site Audit, Tracker,
 * Activity, History — sat at equal weight with no state on them, so arriving at
 * a business was nine decisions and none of them was "what do I do next".
 *
 * The strip becomes a grouped sidebar of FIVE sections. Two merges do the work:
 *
 *   • Site Audit and Pages read the same crawl. Audit groups it by problem,
 *     Pages lists it by URL. That is two lenses, so it is a segmented control
 *     inside one section, not two siblings.
 *   • Activity is the system's log and History is the scan list. Nobody has
 *     ever wanted one without the other.
 *
 * Both merges are pure chrome: each member keeps its own View and its own
 * route, so every existing deep link, `onNavigate` call and ?issue= handoff
 * still resolves exactly where it did. The section is what the sidebar shows;
 * the views are what the router knows.
 */
export interface OuraBusinessSectionView {
    view: View;
    /** Only rendered when a section has more than one member. */
    label: string;
}
export interface OuraBusinessSection {
    id: string;
    label: string;
    icon: LucideIcon;
    /** First entry is the section's landing view. */
    views: OuraBusinessSectionView[];
    /**
     * Views with no place on the rail that are reached from inside this
     * section: the section stays lit while one is open, so staff keep their
     * place. Never listed as a member, so no view switcher offers them.
     */
    alsoActive?: View[];
}
export interface OuraBusinessGroup {
    label: string;
    sections: OuraBusinessSection[];
}

export const OURA_BUSINESS_GROUPS: OuraBusinessGroup[] = [
    {
        // What is true about this business.
        label: "Measure",
        sections: [
            { id: "overview", label: "Overview", icon: LayoutDashboardIcon, views: [{ view: "dashboard", label: "Overview" }] },
            // Search performance opens from the Visibility tiles and stays
            // off the rail (as in the prototype), so it lights this section.
            { id: "visibility", label: "Visibility", icon: EyeIcon, views: [{ view: "questions", label: "Visibility" }], alsoActive: ["search"] },
            {
                id: "site",
                label: "Site",
                icon: GlobeIcon,
                // The Site screen first: it is the lens that names work. The
                // workshop is where you go once you know which page you are
                // fixing. Not "Issues | Pages": the Site screen's crawl card
                // carries its own Issues | Pages tabs, and two controls with
                // the same words would read as one.
                views: [
                    { view: "setup", label: "Site" },
                    { view: "pages", label: "Page workshop" },
                ],
            },
        ],
    },
    {
        // What we are doing about it.
        label: "Act",
        sections: DELIVERY_ON
            ? [
                  // Delivery replaces Fixes and folds in Plan (proposal v1.6, YW 30 Sep 2026):
                  // one line per deliverable, its items in a drawer, the contract and the
                  // timeline inside it. The old routes redirect here, so they stay lit.
                  { id: "delivery", label: "Delivery", icon: PackageIcon, views: [{ view: "delivery", label: "Delivery" }], alsoActive: ["todo", "contract", "timeline"] },
                  { id: "client", label: "Client", icon: MessageSquareIcon, views: [{ view: "reviews", label: "Client" }] },
              ]
            : [
            { id: "fixes", label: "Fixes", icon: ClipboardListIcon, views: [{ view: "todo", label: "Fixes" }] },
            // The approval layer (#3679). It sits in Act rather than Measure
            // because it is work in flight, not a finding: which tasks the
            // merchant has been asked to approve, and what came back.
            { id: "client", label: "Client", icon: MessageSquareIcon, views: [{ view: "reviews", label: "Client" }] },
            // WITHOUT THIS THE TWO SCREENS HAVE NO DOOR (#3776). Their routes
            // resolved and their content was correct, but #3714 added them to
            // `TABS` in BusinessDetail.tsx — the tab strip this sidebar
            // replaced — so on dev and staging nothing linked to them and
            // `ouraSectionFor()` could not highlight them either. The publish
            // flow (#3757) lives on the Timeline tab, so it was unreachable too.
            //
            // Last in Act, because the daily surfaces come first: Fixes is
            // where the work is, Client is the conversation about it, and the
            // contract is what both are measured against — read often, not
            // constantly. Contract is the landing view because the schedule is
            // computed FROM it, and reading them the other way round invites
            // arguing with a date whose reason you have not seen.
            {
                id: "plan",
                label: "Plan",
                icon: FileTextIcon,
                views: [
                    { view: "contract", label: "Contract" },
                    { view: "timeline", label: "Timeline" },
                ],
            },
              ],
    },
    {
        // Set once, read when somebody asks what happened.
        label: "Set up",
        sections: [
            // First in Set up: a new client lands here from the list, and it
            // names the one thing to do next until they are active.
            ...(showOnboarding
                ? [{ id: "onboarding", label: "Onboarding", icon: FlagIcon, views: [{ view: "onboarding" as const, label: "Onboarding" }] }]
                : []),
            // Same missing door as Plan had (#3776): #3684 added Knowledge to
            // `TABS` in BusinessDetail.tsx on the day this sidebar replaced that
            // strip, so the route rendered but nothing linked to it. First in
            // Set up: what the client SENT, before the plumbing that reads it.
            // Documents: Client documents and Knowledge, each its own address, switched by
            // underlined page tabs drawn by the screen (DocumentsTabs), as the merchant's
            // Visibility does. Knowledge rides on `alsoActive` so the rail's segmented
            // switcher does not draw a second set of tabs (Jovi, 21 Oct).
            { id: "documents", label: "Documents", icon: FolderOpenIcon, views: [{ view: "documents", label: "Documents" }], alsoActive: ["knowledge"] },
            { id: "connectors", label: "Connectors", icon: PlugIcon, views: [{ view: "connectors", label: "Connectors" }] },
            {
                id: "history",
                label: "History",
                icon: HistoryIcon,
                views: [
                    { view: "history", label: "Scans" },
                    { view: "activity", label: "Activity" },
                ],
            },
        ],
    },
];

const ALL_SECTIONS = OURA_BUSINESS_GROUPS.flatMap((g) => g.sections);

/**
 * Which sidebar section a view lights: the one it is a member of, else the
 * one that lists it in `alsoActive`. Undefined for a non-business view.
 */
export function ouraSectionFor(view: View): OuraBusinessSection | undefined {
    return (
        ALL_SECTIONS.find((s) => s.views.some((v) => v.view === view))
        ?? ALL_SECTIONS.find((s) => s.alsoActive?.includes(view))
    );
}

/**
 * The sidebar is behind a flag until it has been walked through with the team.
 * Production keeps the tab strip; dev and staging get the sidebar, so the two
 * can be compared on the same data rather than from screenshots.
 */
export const isBusinessSidebarLive: boolean =
    import.meta.env.VITE_ENABLE_OURA_BUSINESS_SIDEBAR === "true" || !isOuraProd();

/**
 * Nav depends on WHO you are:
 *   • client → the single-business tabs.
 *   • staff  → always the pipeline rail (Overview · Businesses). Drilling into
 *     a business no longer swaps the rail — the per-business views are page
 *     tabs inside the detail screen (BusinessDetail), so Oura reads as ONE
 *     platform (gameplan A3).
 */
export function ouraNavFor(role: OuraRole): OuraNavGroup[] {
    // Settings is no longer a nav item — it lives in the user-avatar dropdown at
    // the bottom of the rail, matching the other products.
    if (role === "oura-client") {
        return singleBusinessGroups(role);
    }
    // The Overview roll-up is hidden for now (its headline numbers moved onto
    // the Businesses page as stat tiles) — the route still exists.
    //
    // "AEO Assessment" was removed from the rail: it offered a SECOND way to
    // start a scan alongside the New Scan button on Businesses, which read as
    // two competing entry points for the same job. Businesses + New Scan is now
    // the single path. The /oura/assessment route still resolves (deep links and
    // the prescreen flow it hosts are untouched) — it is just no longer
    // advertised in the nav.
    return [
        {
            label: "Growth",
            items: [
                { view: "businesses", label: "Businesses", icon: Building2Icon, to: ouraHref("businesses", { role }) },
                // Every client's change requests. Sample data until its routes ship.
                ...(REQUESTS_ON ? [{ view: "requests" as const, label: "Requests", icon: MessageSquareIcon, to: ouraHref("requests", { role }) }] : []),
                // The skills Create a doc runs, 1 per doc type (Docs phase 2).
                // Only where the Docs write switch is on, as its route is.
                ...(DOCS_WRITE_ON ? [{ view: "skills" as const, label: "Skills", icon: WandSparklesIcon, to: ouraHref("skills", { role }) }] : []),
                // The Tracker: every client's delivery on one page, wherever Delivery is served.
                ...(DELIVERY_ON ? [{ view: "board" as const, label: "Tracker", icon: ClipboardListIcon, to: ouraHref("board", { role }) }] : []),
            ],
        },
    ];
}
